import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { initializeMessaging, requestNotificationPermission, onForegroundMessage, getFcmToken } from '@/config/firebase';
import { toast } from 'sonner';
import api, { notificationService } from '@/services/api';

const readObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const readLocalizedText = (value, lang = 'en') => {
  if (value == null || value === false) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    return (
      readLocalizedText(value[lang], lang) ||
      readLocalizedText(value.ar, lang) ||
      readLocalizedText(value.en, lang) ||
      readLocalizedText(value.title, lang) ||
      readLocalizedText(value.message, lang) ||
      readLocalizedText(value.body, lang) ||
      ''
    );
  }

  return String(value).trim();
};

const firstText = (lang, ...values) => {
  for (const value of values) {
    const text = readLocalizedText(value, lang);
    if (text) return text;
  }
  return '';
};

const parseNotificationData = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return {};

  try {
    const parsed = JSON.parse(value);
    return readObject(parsed);
  } catch (_) {
    return {};
  }
};

const normalizeNotificationType = (rawType = '') => {
  const type = String(rawType).toLowerCase();
  if (type.includes('appointmentdone') || type.includes('completed') || type.includes('success')) {
    return 'success';
  }
  if (type.includes('appointment') || type.includes('booking') || type.includes('statuschanged')) {
    return 'appointment';
  }
  if (type.includes('message') || type.includes('chat')) {
    return 'message';
  }
  if (type.includes('payment') || type.includes('wallet') || type.includes('invoice')) {
    return 'payment';
  }
  if (type.includes('review') || type.includes('rating')) {
    return 'review';
  }
  if (type.includes('alert') || type.includes('warning') || type.includes('important')) {
    return 'alert';
  }
  return 'alert';
};

const fallbackTitle = (type, rawType, lang) => {
  const isArabic = lang === 'ar';
  const raw = String(rawType || '').toLowerCase();

  if (raw.includes('newappointment')) {
    return isArabic ? '\u0645\u0648\u0639\u062f \u062c\u062f\u064a\u062f' : 'New Appointment';
  }

  const titles = {
    appointment: isArabic ? '\u062a\u062d\u062f\u064a\u062b \u0627\u0644\u0645\u0648\u0639\u062f' : 'Appointment Update',
    success: isArabic ? '\u0627\u0643\u062a\u0645\u0627\u0644 \u0627\u0644\u0645\u0648\u0639\u062f' : 'Appointment Completed',
    message: isArabic ? '\u0631\u0633\u0627\u0644\u0629 \u062c\u062f\u064a\u062f\u0629' : 'New Message',
    payment: isArabic ? '\u062a\u062d\u062f\u064a\u062b \u0627\u0644\u062f\u0641\u0639' : 'Payment Update',
    review: isArabic ? '\u062a\u0642\u064a\u064a\u0645 \u062c\u062f\u064a\u062f' : 'New Review',
    alert: isArabic ? '\u0625\u0634\u0639\u0627\u0631' : 'Notification',
  };

  return titles[type] || titles.alert;
};

const fallbackMessage = (type, data, lang) => {
  const appointmentId = data.appointment_id || data.appointmentId || data.appointment;
  if (!appointmentId) return '';

  if (type === 'success') {
    return lang === 'ar'
      ? `\u062a\u0645 \u0627\u0643\u062a\u0645\u0627\u0644 \u0645\u0648\u0639\u062f\u0643 \u0631\u0642\u0645 ${appointmentId}`
      : `Your appointment #${appointmentId} is completed`;
  }

  if (type === 'appointment') {
    return lang === 'ar'
      ? `\u062a\u0645 \u062a\u062d\u062f\u064a\u062b \u062d\u0627\u0644\u0629 \u0645\u0648\u0639\u062f\u0643 \u0631\u0642\u0645 ${appointmentId}`
      : `Your appointment #${appointmentId} status has been updated`;
  }

  return '';
};

const parseNotificationDate = (value) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const extractNotificationRows = (response) => {
  const payload = response?.data;
  const candidates = [
    payload?.data?.notifications,
    payload?.data?.data,
    payload?.data?.items,
    payload?.data?.records,
    payload?.data?.results,
    payload?.data,
    payload?.notifications,
    payload?.items,
    payload?.records,
    payload?.results,
    payload,
  ];

  return candidates.find(Array.isArray) || [];
};

const normalizeNotification = (notification, index, lang = 'en') => {
  const data = parseNotificationData(notification.data);
  const rawType = notification.type || data.type || data.notification_type || '';
  const type = normalizeNotificationType(rawType);
  const time = parseNotificationDate(
    notification.created_at ||
    notification.createdAt ||
    notification.timestamp ||
    notification.time ||
    notification.date
  );
  const title =
    firstText(
      lang,
      notification.title,
      notification.subject,
      notification.name,
      notification.title_ar && { ar: notification.title_ar, en: notification.title_en },
      data.title,
      data.subject,
      data.name,
      data.title_ar && { ar: data.title_ar, en: data.title_en }
    ) || fallbackTitle(type, rawType, lang);
  const body =
    firstText(
      lang,
      notification.body,
      notification.message,
      notification.description,
      notification.content,
      notification.message_ar && { ar: notification.message_ar, en: notification.message_en },
      data.body,
      data.message,
      data.description,
      data.content,
      data.message_ar && { ar: data.message_ar, en: data.message_en }
    ) || fallbackMessage(type, data, lang);
  const readAt = notification.read_at || notification.readAt || notification.seen_at;
  const explicitRead = notification.read ?? notification.is_read ?? notification.seen;
  const id =
    notification.id ||
    notification.notification_id ||
    notification.uuid ||
    `${time.getTime()}-${index}`;
  const url = data.url || data.link || data.path || notification.url || notification.link || '';

  return {
    id,
    type,
    title,
    body,
    message: body,
    read: Boolean(readAt || explicitRead),
    timestamp: time.toISOString(),
    time,
    data: {
      ...data,
      url,
    },
  };
};

const normalizeNotificationsResponse = (response, lang) =>
  extractNotificationRows(response).map((notification, index) =>
    normalizeNotification(notification, index, lang)
  );

const DEFAULT_NOTIFICATIONS_LIMIT = 20;

const readNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const extractPaginationMeta = (response, notifications, requestedLimit, requestedOffset) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  const isPayloadObject = payload && typeof payload === 'object' && !Array.isArray(payload);
  const limit = readNumber(
    isPayloadObject ? (payload.limit ?? payload.per_page) : undefined,
    requestedLimit
  );
  const offset = readNumber(isPayloadObject ? payload.offset : undefined, requestedOffset);
  const totalValue = isPayloadObject ? (payload.total ?? payload.total_count) : undefined;
  const total = Number.isFinite(Number(totalValue)) ? Number(totalValue) : null;
  const fallbackNextOffset = offset + notifications.length;
  const nextOffsetValue = isPayloadObject ? payload.next_offset : undefined;
  const nextOffset = Number.isFinite(Number(nextOffsetValue))
    ? Number(nextOffsetValue)
    : fallbackNextOffset;
  const hasMore = typeof payload.has_more === 'boolean'
    ? payload.has_more
    : total !== null
      ? fallbackNextOffset < total
      : notifications.length >= limit;

  return {
    limit,
    offset,
    total,
    nextOffset,
    hasMore,
  };
};

const mergeNotifications = (existingNotifications, nextNotifications) => {
  const byId = new Map();
  [...existingNotifications, ...nextNotifications].forEach((notification) => {
    byId.set(String(notification.id), notification);
  });
  return Array.from(byId.values());
};

const extractUnreadCount = (response, notifications) => {
  const payload = response?.data?.data ?? response?.data;
  const candidates = [
    payload?.unread_count,
    payload?.unreadCount,
    payload?.unread,
    payload?.count,
    payload?.total_unread,
    payload,
  ];
  const resolved = candidates.find((value) => {
    const count = Number(value);
    return Number.isFinite(count);
  });

  if (resolved !== undefined) {
    return Number(resolved);
  }

  return notifications.filter((notification) => !notification.read).length;
};

const markNotificationReadOnBackend = async (id) => {
  try {
    return await notificationService.markAsRead(id);
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 405) {
      return api.put(`/notifications/${id}`);
    }
    throw error;
  }
};

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('ar')
    ? 'ar'
    : 'en';
  const [fcmToken, setFcmToken] = useState(null);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const [notificationsPagination, setNotificationsPagination] = useState({
    limit: DEFAULT_NOTIFICATIONS_LIMIT,
    offset: 0,
    total: null,
    nextOffset: 0,
    hasMore: false,
  });
  const [isSupported, setIsSupported] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastRegisteredUserId, setLastRegisteredUserId] = useState(null);
  
  // Refs to prevent duplicate API calls
  const isRegisteringToken = useRef(false);
  const seenNotificationIds = useRef(new Set());
  const lastTokenRegistrationTime = useRef(0);
  const TOKEN_REGISTRATION_COOLDOWN = 60000; // 1 minute cooldown between registrations

  const fetchNotifications = useCallback(async ({
    limit = DEFAULT_NOTIFICATIONS_LIMIT,
    offset = 0,
    append = false,
  } = {}) => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setNotificationsError(null);
      setNotificationsLoading(false);
      setNotificationsLoadingMore(false);
      setNotificationsPagination({
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
        offset: 0,
        total: null,
        nextOffset: 0,
        hasMore: false,
      });
      return [];
    }

    if (append) {
      setNotificationsLoadingMore(true);
    } else {
      setNotificationsLoading(true);
    }
    setNotificationsError(null);

    try {
      const [listResponse, countResponse] = await Promise.all([
        notificationService.getAll({ limit, offset }),
        notificationService.getCount().catch(() => null),
      ]);
      const nextNotifications = normalizeNotificationsResponse(listResponse, lang);
      const pagination = extractPaginationMeta(
        listResponse,
        nextNotifications,
        limit,
        offset
      );

      setNotifications((currentNotifications) =>
        append
          ? mergeNotifications(currentNotifications, nextNotifications)
          : nextNotifications
      );
      setNotificationsPagination(pagination);
      setUnreadCount(extractUnreadCount(countResponse || listResponse, nextNotifications));
      return nextNotifications;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotificationsError(error);
      return [];
    } finally {
      if (append) {
        setNotificationsLoadingMore(false);
      } else {
        setNotificationsLoading(false);
      }
    }
  }, [isAuthenticated, lang]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const loadMoreNotifications = useCallback(() => {
    if (
      !isAuthenticated ||
      notificationsLoading ||
      notificationsLoadingMore ||
      !notificationsPagination.hasMore
    ) {
      return Promise.resolve([]);
    }

    return fetchNotifications({
      limit: notificationsPagination.limit || DEFAULT_NOTIFICATIONS_LIMIT,
      offset: notificationsPagination.nextOffset,
      append: true,
    });
  }, [
    fetchNotifications,
    isAuthenticated,
    notificationsLoading,
    notificationsLoadingMore,
    notificationsPagination,
  ]);

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'Notification' in window && 
                        'serviceWorker' in navigator && 
                        'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Initialize FCM when user logs in
  useEffect(() => {
    if (isAuthenticated && isSupported && !isInitialized) {
      initializeFCM();
    }
  }, [isAuthenticated, isSupported, isInitialized]);

  // Listen for foreground messages
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;

    const unsubscribe = onForegroundMessage((payload) => {
      handleForegroundNotification(payload);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isInitialized, isAuthenticated]);

  // Listen for service worker messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        console.log('Notification clicked:', event.data);
        // Handle navigation or state updates here
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  const initializeFCM = async () => {
    try {
      await initializeMessaging();
      setIsInitialized(true);
      console.log('FCM initialized successfully');
    } catch (error) {
      console.error('Failed to initialize FCM:', error);
    }
  };

  const sendTokenToBackend = useCallback(async (token) => {
    // Prevent duplicate/rapid API calls
    const now = Date.now();
    if (isRegisteringToken.current) {
      console.log('FCM token registration already in progress, skipping...');
      return;
    }
    if (now - lastTokenRegistrationTime.current < TOKEN_REGISTRATION_COOLDOWN) {
      console.log('FCM token registration on cooldown, skipping...');
      return;
    }
    
    // Don't call API if no auth token exists
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.log('No auth token found, skipping device token registration...');
      return;
    }
    
    isRegisteringToken.current = true;
    
    try {
      const formData = new FormData();
      formData.append('device_token', token);
      formData.append('platform', 'web');
      formData.append('locale', lang === 'ar' ? 'ar_SY' : 'en_US');

      await api.post('/update-device-token', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json',
        },
      });

      lastTokenRegistrationTime.current = now;
      console.log('FCM token registered with backend');
    } catch (error) {
      // Silently ignore 401/429 errors - don't disrupt user experience
      const status = error.response?.status;
      if (status === 401 || status === 429) {
        console.warn(`Device token registration failed with ${status}, ignoring...`);
      } else {
        console.error('Failed to register FCM token:', error);
      }
      // Don't throw - token still works for local notifications
    } finally {
      isRegisteringToken.current = false;
    }
  }, [lang]);

  // Register token automatically if already granted after login
  // Uses refs to prevent duplicate API calls
  useEffect(() => {
    if (!isAuthenticated || !isSupported || permission !== 'granted') return;
    if (!user?.id) return;

    // Already registered for this user and token exists
    if (fcmToken && lastRegisteredUserId === user.id) {
      return;
    }
    
    // Prevent running if already in progress
    if (isRegisteringToken.current) {
      return;
    }

    const ensureTokenRegistered = async () => {
      const result = await getFcmToken();
      if (result.permission === 'granted' && result.token) {
        setFcmToken(result.token);
        await sendTokenToBackend(result.token);
        setLastRegisteredUserId(user.id);
      }
    };

    ensureTokenRegistered();
  }, [isAuthenticated, isSupported, permission, user?.id]); // Removed fcmToken, lastRegisteredUserId, sendTokenToBackend from deps

  const handleForegroundNotification = (payload) => {
    const { notification, data } = payload;
    const notificationId = data?.notification_id || data?.notificationId || data?.id;
    if (notificationId && seenNotificationIds.current.has(String(notificationId))) return;
    if (notificationId) seenNotificationIds.current.add(String(notificationId));
    const title = notification?.title || data?.title || 'New Notification';
    const body = notification?.body || data?.body || '';
    const type = data?.type || 'general';
    
    // Add to notifications list
    const now = new Date();
    const newNotification = {
      id: data?.id || data?.notification_id || Date.now(),
      title,
      body,
      message: body,
      type,
      data,
      read: false,
      timestamp: now.toISOString(),
      time: now
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    setNotificationsPagination(prev => ({
      ...prev,
      total: prev.total === null ? null : prev.total + 1,
      nextOffset: prev.nextOffset + 1,
    }));
    
    // Show toast notification
    const toastOptions = {
      description: body,
      duration: 5000,
      action: data?.url ? {
        label: 'View',
        onClick: () => window.location.href = data.url
      } : undefined
    };
    
    switch (type) {
      case 'appointment':
        toast.info(title, { ...toastOptions, icon: '📅' });
        break;
      case 'message':
        toast.info(title, { ...toastOptions, icon: '💬' });
        break;
      case 'payment':
        toast.success(title, { ...toastOptions, icon: '💰' });
        break;
      case 'review':
        toast.info(title, { ...toastOptions, icon: '⭐' });
        break;
      case 'alert':
        toast.warning(title, { ...toastOptions, icon: '⚠️' });
        break;
      default:
        toast(title, toastOptions);
    }
    
    // Show browser notification if page is not focused
    if (document.hidden && permission === 'granted') {
      showBrowserNotification(title, body, data);
    }
  };

  useEffect(() => {
    const handleRealtimeNotification = (event) => {
      const payload = event.detail || {};
      const title = lang === 'ar'
        ? (payload.title_ar || payload.title)
        : (payload.title_en || payload.title);
      const body = lang === 'ar'
        ? (payload.message_ar || payload.message || payload.body)
        : (payload.message_en || payload.message || payload.body);
      handleForegroundNotification({
        notification: { title, body },
        data: {
          ...(payload.metadata || {}),
          ...payload,
          notification_id: payload.notification_id || payload.id,
        },
      });
    };
    window.addEventListener('dactrha:notification', handleRealtimeNotification);
    return () => window.removeEventListener('dactrha:notification', handleRealtimeNotification);
  }, [lang, permission]);

  const showBrowserNotification = (title, body, data = {}) => {
    if (permission !== 'granted') return;
    
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: data.tag || 'dactrha-notification',
      data
    });
    
    notification.onclick = () => {
      window.focus();
      if (data.url) {
        window.location.href = data.url;
      }
      notification.close();
    };
  };

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Notifications are not supported in this browser');
      return { success: false, error: 'Not supported' };
    }
    
    try {
      const result = await requestNotificationPermission();
      setPermission(result.permission);
      
      if (result.permission === 'granted' && result.token) {
        setFcmToken(result.token);
        
        // Send token to backend
        await sendTokenToBackend(result.token);
        setLastRegisteredUserId(user?.id || null);
        
        toast.success('Notifications enabled successfully!');
        return { success: true, token: result.token };
      } else if (result.permission === 'denied') {
        toast.error('Notification permission was denied. Please enable it in browser settings.');
        return { success: false, error: 'Permission denied' };
      } else {
        toast.warning('Could not enable notifications. Please try again.');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to enable notifications');
      return { success: false, error };
    }
  }, [isSupported, sendTokenToBackend, user?.id]);

  

  const markAsRead = useCallback(async (notificationId) => {
    const target = notifications.find(
      (notification) => String(notification.id) === String(notificationId)
    );
    if (!target || target.read) return;

    setNotifications(prev => 
      prev.map(n => 
        String(n.id) === String(notificationId) ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await markNotificationReadOnBackend(notificationId);
    } catch (error) {
      console.warn('Failed to mark notification as read:', error);
    }
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    const hasUnreadNotifications = notifications.some((notification) => !notification.read);
    if (!hasUnreadNotifications) return;

    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.warn('Failed to mark all notifications as read:', error);
    }
  }, [notifications]);

  const deleteNotification = useCallback(async (notificationId) => {
    const target = notifications.find(
      (notification) => String(notification.id) === String(notificationId)
    );

    setNotifications(prev => prev.filter(
      (notification) => String(notification.id) !== String(notificationId)
    ));

    if (target && !target.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotificationsPagination(prev => ({
      ...prev,
      total: prev.total === null ? null : Math.max(0, prev.total - 1),
      nextOffset: Math.max(0, prev.nextOffset - 1),
    }));

    try {
      await notificationService.delete(notificationId);
    } catch (error) {
      console.warn('Failed to delete notification:', error);
    }
  }, [notifications]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Test notification function (for development)
  const sendTestNotification = useCallback(() => {
    handleForegroundNotification({
      notification: {
        title: 'Test Notification',
        body: 'This is a test notification from Dactrha'
      },
      data: {
        type: 'appointment',
        url: '/appointments'
      }
    });
  }, []);

  const value = {
    // State
    fcmToken,
    permission,
    notifications,
    unreadCount,
    notificationsLoading,
    notificationsLoadingMore,
    notificationsError,
    notificationsHasMore: notificationsPagination.hasMore,
    notificationsTotal: notificationsPagination.total,
    isSupported,
    isInitialized,
    
    // Actions
    refreshNotifications: fetchNotifications,
    loadMoreNotifications,
    requestPermission,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearNotifications,
    sendTestNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
